import { FaShoppingCart, FaUsers } from 'react-icons/fa';
import { MdOutlinePendingActions } from 'react-icons/md';
import { BsCashCoin, BsPercent, BsReceipt } from 'react-icons/bs';
import Format from '../../../Services/Format';
import type { TemporaryGroup } from '../useSalesTemporaryTotal/summarizeTemporary';
import * as S from './styles';

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

export function KpiCards({
  totalValue,
  averageTicket,
  totalOrders,
  serviceTaxTotal,
  serviceTaxTables,
  serviceTaxDelivery,
  temporaryTotal,
  temporaryCount,
  temporaryTables,
  temporaryDelivery,
  customersCount,
  newCustomers,
  isLoading,
}: {
  totalValue: number;
  averageTicket: number;
  totalOrders: number;
  serviceTaxTotal: number;
  /** null quando a API não devolve a divisão por origem. */
  serviceTaxTables: number | null;
  serviceTaxDelivery: number | null;
  temporaryTotal: number;
  temporaryCount: number;
  temporaryTables: TemporaryGroup;
  temporaryDelivery: TemporaryGroup;
  customersCount: number;
  newCustomers: number;
  /** Primeira carga: mostrar "—" em vez de zeros, que pareceriam dados reais. */
  isLoading?: boolean;
}) {
  const money = (value: number) =>
    isLoading ? '—' : Format.formatCurrencyBr(String(value));

  const amount = (value: number) =>
    isLoading ? '—' : value.toLocaleString('pt-BR');

  return (
    <S.Container>
      <S.Card>
        <div className="top">
          <span className="title">Faturamento do Período</span>
          <BsCashCoin className="icon" size={18} />
        </div>
        <span className="value">{money(totalValue)}</span>
        <span className="caption">Vendas fechadas no período</span>
      </S.Card>

      <S.Card>
        <div className="top">
          <span className="title">Ticket Médio</span>
          <BsReceipt className="icon" size={18} />
        </div>
        <span className="value">{money(averageTicket)}</span>
        <span className="caption">Faturamento dividido por pedidos</span>
      </S.Card>

      <S.Card>
        <div className="top">
          <span className="title">Total de Pedidos</span>
          <FaShoppingCart className="icon" size={18} />
        </div>
        <span className="value">{amount(totalOrders)}</span>
        <span className="caption">Pedidos no período</span>
      </S.Card>

      <S.Card>
        <div className="top">
          <span className="title">Taxa de Serviço</span>
          <BsPercent className="icon" size={18} />
        </div>
        <span className="value">{money(serviceTaxTotal)}</span>
        <span className="caption">Total cobrado no período</span>
        {serviceTaxTables !== null && serviceTaxDelivery !== null && (
          <dl className="split">
            <div>
              <dt>Mesas</dt>
              <dd>{money(serviceTaxTables)}</dd>
            </div>
            <div>
              <dt>Delivery</dt>
              <dd>{money(serviceTaxDelivery)}</dd>
            </div>
          </dl>
        )}
      </S.Card>

      <S.Card highlight>
        <div className="top">
          <span className="title">Vendas Temporárias</span>
          <MdOutlinePendingActions className="icon" size={18} />
        </div>
        <span className="value">{money(temporaryTotal)}</span>
        <span className="caption">
          {isLoading
            ? 'Calculando vendas em aberto…'
            : `${plural(temporaryCount, 'venda')} em aberto agora`}
        </span>
        <dl className="split">
          <div>
            <dt title={isLoading ? undefined : plural(temporaryTables.count, 'mesa')}>
              Mesas{isLoading ? '' : ` (${temporaryTables.count})`}
            </dt>
            <dd>{money(temporaryTables.total)}</dd>
          </div>
          <div>
            <dt title={isLoading ? undefined : plural(temporaryDelivery.count, 'pedido')}>
              Delivery{isLoading ? '' : ` (${temporaryDelivery.count})`}
            </dt>
            <dd>{money(temporaryDelivery.total)}</dd>
          </div>
        </dl>
      </S.Card>

      <S.Card>
        <div className="top">
          <span className="title">Novos Clientes</span>
          <FaUsers className="icon" size={18} />
        </div>
        <span className="value">{amount(newCustomers)}</span>
        <span className="caption">
          {isLoading
            ? 'Carregando clientes do período…'
            : `De ${customersCount.toLocaleString('pt-BR')} atendidos no período`}
        </span>
      </S.Card>
    </S.Container>
  );
}
